import React, { useState, useEffect, useRef, useCallback } from "react";

export const useAntiCheat = ({
  isActive = false,
  maxWarnings = 3,
  onAutoSubmit = () => {},
}) => {
  const [warnings, setWarnings] = useState(0);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [isAutoSubmitted, setIsAutoSubmitted] = useState(false);
  const [autoSubmitReason, setAutoSubmitReason] = useState("");

  const [showRulesModal, setShowRulesModal] = useState(false);
  const hasAcknowledgedRulesRef = useRef(false);
  const showRulesModalRef = useRef(false);
  showRulesModalRef.current = showRulesModal;

  const warningsRef = useRef(0);
  warningsRef.current = warnings;

  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  const onAutoSubmitRef = useRef(onAutoSubmit);
  onAutoSubmitRef.current = onAutoSubmit;

  // Handle active status and initial rules modal
  useEffect(() => {
    if (isActive) {
      if (!hasAcknowledgedRulesRef.current) {
        setShowRulesModal(true);
      } else if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen?.().catch(() => {});
      }
    } else {
      setShowRulesModal(false);
    }
  }, [isActive]);

  const acknowledgeRules = () => {
    hasAcknowledgedRulesRef.current = true;
    setShowRulesModal(false);
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }
  };

  const resetRulesModal = () => {
    hasAcknowledgedRulesRef.current = false;
    setShowRulesModal(false);
  };

  useEffect(() => {
    if (!isActive) return;

    const triggerWarning = (reason) => {
      if (!isActiveRef.current || showRulesModalRef.current || !hasAcknowledgedRulesRef.current) return;
      const nextWarnings = warningsRef.current + 1;
      setWarnings(nextWarnings);
      setWarningMessage(reason);

      if (nextWarnings >= maxWarnings) {
        setShowWarningModal(false);
        setIsAutoSubmitted(true);
        setAutoSubmitReason(reason);
        if (onAutoSubmitRef.current) {
          onAutoSubmitRef.current();
        }
      } else {
        setShowWarningModal(true);
      }
    };

    // 1. Tab visibility change (switching tab / minimizing)
    const handleVisibilityChange = () => {
      if (document.hidden && isActiveRef.current) {
        triggerWarning(
          "Tab switching or minimizing the browser window is prohibited during the test."
        );
      }
    };

    // 2. Window blur (focus lost to desktop or external app)
    const handleWindowBlur = () => {
      if (isActiveRef.current) {
        triggerWarning(
          "Browser window lost focus. Please remain focused on the test window."
        );
      }
    };

    // 3. Fullscreen exit check
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isActiveRef.current) {
        triggerWarning("Fullscreen mode exited.");
      }
    };

    // 4. Disable Right Click Context Menu
    const handleContextMenu = (e) => {
      if (isActiveRef.current) {
        e.preventDefault();
      }
    };

    // 5. Disable Copy, Cut, Paste, Select All
    const handleCopyPaste = (e) => {
      if (isActiveRef.current) {
        e.preventDefault();
      }
    };

    // 6. Disable Developer Shortcuts (F12, Ctrl+Shift+I/J/C, Ctrl+U, Ctrl+C/V/X/A/P/S)
    const handleKeyDown = (e) => {
      if (!isActiveRef.current) return;

      const isF12 = e.key === "F12" || e.keyCode === 123;
      const isCtrlShiftKey =
        e.ctrlKey &&
        e.shiftKey &&
        ["I", "J", "C", "i", "j", "c"].includes(e.key);
      const isCtrlKey =
        e.ctrlKey &&
        ["u", "U", "c", "C", "v", "V", "x", "X", "a", "A", "p", "P", "s", "S"].includes(
          e.key
        );

      if (isF12 || isCtrlShiftKey || isCtrlKey) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("copy", handleCopyPaste);
    document.addEventListener("cut", handleCopyPaste);
    document.addEventListener("paste", handleCopyPaste);
    document.addEventListener("selectstart", handleCopyPaste);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("copy", handleCopyPaste);
      document.removeEventListener("cut", handleCopyPaste);
      document.removeEventListener("paste", handleCopyPaste);
      document.removeEventListener("selectstart", handleCopyPaste);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isActive, maxWarnings]);

  const reEnterFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }
    setShowWarningModal(false);
  };

  const closeAutoSubmitModal = () => {
    setIsAutoSubmitted(false);
  };

  return {
    warnings,
    showWarningModal,
    warningMessage,
    isAutoSubmitted,
    autoSubmitReason,
    showRulesModal,
    acknowledgeRules,
    resetRulesModal,
    setShowWarningModal,
    reEnterFullscreen,
    closeAutoSubmitModal,
  };
};

export const useLiveQuizLock = (isLiveActive = false) => {
  const [isNeedsFullscreen, setIsNeedsFullscreen] = useState(false);
  const isLiveActiveRef = useRef(isLiveActive);
  isLiveActiveRef.current = isLiveActive;

  const tryFullscreen = useCallback(() => {
    if (isLiveActiveRef.current && !document.fullscreenElement) {
      document.documentElement.requestFullscreen?.()
        .then(() => setIsNeedsFullscreen(false))
        .catch(() => setIsNeedsFullscreen(true));
    } else if (document.fullscreenElement) {
      setIsNeedsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    if (!isLiveActive) {
      setIsNeedsFullscreen(false);
      return;
    }

    tryFullscreen();

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isLiveActiveRef.current) {
        setIsNeedsFullscreen(true);
        tryFullscreen();
      } else if (document.fullscreenElement) {
        setIsNeedsFullscreen(false);
      }
    };

    const handleFocusOrVisibility = () => {
      if (isLiveActiveRef.current) {
        tryFullscreen();
      }
    };

    const handleUserInteraction = () => {
      if (isLiveActiveRef.current && !document.fullscreenElement) {
        tryFullscreen();
      }
    };

    const handleKeyDown = (e) => {
      if (!isLiveActiveRef.current) return;

      const isEscape = e.key === "Escape" || e.keyCode === 27;
      const isF11 = e.key === "F11" || e.keyCode === 122;
      const isF12 = e.key === "F12" || e.keyCode === 123;
      const isCtrlShiftKey =
        e.ctrlKey &&
        e.shiftKey &&
        ["I", "J", "C", "i", "j", "c"].includes(e.key);
      const isCtrlKey =
        e.ctrlKey &&
        ["u", "U", "c", "C", "v", "V", "x", "X", "a", "A", "p", "P", "s", "S"].includes(
          e.key
        );

      if (isEscape || isF11 || isF12 || isCtrlShiftKey || isCtrlKey) {
        e.preventDefault();
        e.stopPropagation();
        tryFullscreen();
      }
    };

    const handlePreventDefault = (e) => {
      if (isLiveActiveRef.current) {
        e.preventDefault();
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    window.addEventListener("focus", handleFocusOrVisibility);
    document.addEventListener("visibilitychange", handleFocusOrVisibility);
    window.addEventListener("click", handleUserInteraction, true);
    window.addEventListener("pointerdown", handleUserInteraction, true);
    window.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("contextmenu", handlePreventDefault);
    document.addEventListener("copy", handlePreventDefault);
    document.addEventListener("cut", handlePreventDefault);
    document.addEventListener("paste", handlePreventDefault);
    document.addEventListener("selectstart", handlePreventDefault);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      window.removeEventListener("focus", handleFocusOrVisibility);
      document.removeEventListener("visibilitychange", handleFocusOrVisibility);
      window.removeEventListener("click", handleUserInteraction, true);
      window.removeEventListener("pointerdown", handleUserInteraction, true);
      window.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("contextmenu", handlePreventDefault);
      document.removeEventListener("copy", handlePreventDefault);
      document.removeEventListener("cut", handlePreventDefault);
      document.removeEventListener("paste", handlePreventDefault);
      document.removeEventListener("selectstart", handlePreventDefault);
    };
  }, [isLiveActive, tryFullscreen]);

  return {
    isNeedsFullscreen,
    tryFullscreen,
  };
};

export const LiveQuizFullscreenGuard = ({ onReEnter }) => {
  return (
    <div className="anti_cheat_overlay live_fullscreen_guard_overlay" onClick={onReEnter}>
      <div className="anti_cheat_card live_fullscreen_guard_card">
        <div className="live_guard_icon">
          <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-2V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
          </svg>
        </div>
        <h3 className="rules_title">Fullscreen Mode Required</h3>
        <p className="anti_cheat_desc">
          Live exam must be taken in full-screen mode. Click anywhere on this screen to re-enter full screen and resume your test.
        </p>
        <button className="anti_cheat_resume_btn rules_start_btn" onClick={onReEnter}>
          <span>Click to Re-Enter Fullscreen</span>
        </button>
      </div>
    </div>
  );
};

export const AntiCheatRulesModal = ({ maxWarnings = 3, onStart }) => {
  const [isChecked, setIsChecked] = useState(false);

  return (
    <div className="anti_cheat_overlay">
      <div className="anti_cheat_card anti_cheat_rules_card">
        <h3 className="rules_title">Anti-Cheat & Auto-Submit System</h3>
        <p className="anti_cheat_desc">
          To maintain exam integrity, this test is monitored automatically. Please refrain from the following prohibited actions:
        </p>

        <div className="rules_list">
          <div className="rule_item">
            <div className="rule_icon forbidden">
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
            </div>
            <div className="rule_content">
              <strong>Tab Switching & Minimizing</strong>
              <span>Do not switch browser tabs or minimize the window during the exam.</span>
            </div>
          </div>

          <div className="rule_item">
            <div className="rule_icon forbidden">
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 8V4m0 0h4M4 4l5 5m11-2V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </div>
            <div className="rule_content">
              <strong>Window Focus & Fullscreen Mode</strong>
              <span>Remain in full-screen mode and keep the exam window active at all times.</span>
            </div>
          </div>

          <div className="rule_item">
            <div className="rule_icon forbidden">
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </div>
            <div className="rule_content">
              <strong>Copy/Paste & Shortcut Keys</strong>
              <span>Right-clicking, copying content, and developer tool hotkeys (F12) are strictly blocked.</span>
            </div>
          </div>
        </div>

        <div className="rules_enforcement_box">
          <div className="enforcement_icon">⚡</div>
          <div className="enforcement_text">
            <strong>Auto-Submit Limit:</strong> If you reach <strong>{maxWarnings} warnings</strong> due to policy violations, your exam will be <strong>automatically submitted</strong> by the system.
          </div>
        </div>

        <label className="rules_checkbox_container">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
          />
          <span>I have read and understood all the test rules and security instructions.</span>
        </label>

        <button
          className={`anti_cheat_resume_btn rules_start_btn ${!isChecked ? "disabled" : ""}`}
          onClick={onStart}
          disabled={!isChecked}
        >
          <span>I Understand & Start Test</span>
        </button>
      </div>
    </div>
  );
};

export const AntiCheatModal = ({
  warnings,
  maxWarnings = 3,
  message,
  onResume,
}) => {
  return (
    <div className="anti_cheat_overlay">
      <div className="anti_cheat_card">
        <div className="anti_cheat_header_badge">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>ANTI-CHEAT WARNING ({warnings} / {maxWarnings})</span>
        </div>

        <h3>Test Violation Detected</h3>
        <p className="anti_cheat_desc">{message}</p>

        {warnings >= maxWarnings ? (
          <div className="anti_cheat_limit_reached">
            <p>Maximum warning limit reached ({warnings}/{maxWarnings}). Your test is automatically submitting...</p>
          </div>
        ) : (
          <button className="anti_cheat_resume_btn" onClick={onResume}>
            <span>I Understand & Resume Test</span>
          </button>
        )}
      </div>
    </div>
  );
};

export const AutoSubmittedModal = ({ reason, onClose }) => {
  return (
    <div className="anti_cheat_overlay">
      <div className="anti_cheat_card auto_submit_card">
        <div className="anti_cheat_header_badge auto_submit_badge">
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>TEST AUTO-SUBMITTED BY SYSTEM</span>
        </div>

        <h3>Test Submitted Automatically</h3>
        <p className="anti_cheat_desc">
          Your test was automatically submitted by the system due to security policy enforcement.
        </p>

        <div className="auto_submit_reason_box">
          <span className="reason_label">REASON FOR SUBMISSION:</span>
          <p className="reason_text">{reason || "Multiple anti-cheat violations detected (Tab switching / Exited fullscreen / Window blur)."}</p>
        </div>

        <button className="anti_cheat_resume_btn auto_submit_btn" onClick={onClose}>
          <span>View Final Scores</span>
        </button>
      </div>
    </div>
  );
};
